import React from 'react';

import { useLocation } from 'react-router-dom';
import {
  ShoppingCart,
  CircleDollarSign,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';

import { canCreateOnRoute } from '../../util/permissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { IconComponent } from '../../util/icons';
import logo from '../../asset/images/logo.png';
import { useNavigationService } from '../../services/navigate-service';
import { ROUTES } from '../../config/routes';
import { TreeSelect as TreeSelectUI } from '@/components/ui/tree-select';
import { UserMenu } from '@/components/ui/user-menu';
import { useCartBadgeCount } from '@/hooks/useCartBadgeCount';
import { useEntityTreeNodes } from '@/hooks/useEntityTreeNodes';
import { usePrepaySaveGate } from '@/hooks/usePrepaySaveGate';
import { cn } from '@/lib/utils';

interface AppHeaderProps {
  isLogin: boolean;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

/* ------------------------------------------------------------------ */
/*  AppHeader (main component)                                         */
/* ------------------------------------------------------------------ */
const AppHeader = ({
  isLogin,
  setIsLoading,
  sidebarCollapsed,
  onToggleSidebar
}: AppHeaderProps) => {
  const { navigateTo } = useNavigationService();
  const location = useLocation();
  const treeNodes = useEntityTreeNodes();
  const { cartCount, showBadge } = useCartBadgeCount(isLogin);

  const isCartPage = location.pathname === ROUTES.ORDERS_CART;
  const showPrepayLink = usePrepaySaveGate(isLogin);

  const handleCartClick = () => {
    navigateTo(ROUTES.ORDERS_CART);
  };

  return (
    <header className="relative shrink-0 flex justify-between items-center gap-4 border border-border h-[60px] w-full z-40 bg-background text-base font-medium text-foreground">
      {/* Sidebar toggle + Logo + entity tree. min-w-0 lets the tree give way
          when the header is tight (DV-3474); the toggle and logo don't. */}
      <div className="flex items-center min-w-0">
        {onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="flex shrink-0 items-center justify-center ml-3 border-none bg-transparent p-2 text-foreground transition-colors hover:text-muted-foreground cursor-pointer"
            aria-label={
              sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
            }
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </button>
        )}
        <button
          className="relative inline shrink-0 h-[inherit] ml-3 border-none bg-transparent w-[184px] box-border mr-9 p-0 cursor-pointer"
          onClick={() => window.location.assign('/home/properties')}
        >
          <IconComponent src={logo} alt="logo" />
        </button>
        <TreeSelectUI
          nodes={treeNodes}
          md="9"
          controlId="myTreeSelect"
          type="text"
          ariaDescribedBy="entityHelpBlock"
          placeholder="Search Entity..."
          name="entityTree"
          setIsLoading={setIsLoading}
        />
      </div>

      {/* Right-side controls (visible when logged in) */}
      {isLogin && (
        <div className="flex shrink-0 items-center gap-4 pr-3">
          {/* Prepay & Save link — hidden for Emma-category accounts, and
              withheld until the category lookup settles (see showPrepayLink) */}
          {showPrepayLink && (
            <a
              href="https://www.planomatic.com/prepay-save"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-foreground no-underline transition-colors hover:opacity-80"
            >
              <CircleDollarSign
                className="h-6 w-6"
                fill="currentColor"
                stroke="white"
                strokeWidth={1.5}
              />
              Prepay & Save 25%
            </a>
          )}

          {/* Order cart button — hidden for roles the cart route rejects
              (ProtectedRoute gates ROUTES.ORDERS_CART on the same predicate),
              so the button can never be a one-click path to /error.
              Icon-only below xl (DV-3474), so the entity tree gives up less
              width there; aria-label keeps the name when the text is hidden. */}
          {canCreateOnRoute('Order') && (
            <div
              className={cn(
                'flex items-center',
                isCartPage && 'border-b-2 border-primary'
              )}
            >
              <Button
                variant="ghost"
                data-testid="Order cart"
                aria-label="Order Cart"
                className="relative"
                onClick={handleCartClick}
              >
                <ShoppingCart className="h-6 w-6 text-foreground" />
                <span className="hidden xl:inline">Order Cart</span>
                {showBadge && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center rounded-full text-[10px] p-0.5"
                  >
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </div>
          )}

          {/* User dropdown */}
          <UserMenu />
        </div>
      )}
    </header>
  );
};

export default AppHeader;
